<template>
  <div>
    <v-row>
      <v-col cols="12">
        <p class="text-2xl mb-0">
          Relatório Diário (SMS)
        </p>
        <p class="text-sm text--secondary">
          Visualização de entregas, falhas e status dos SMS do dia
        </p>
      </v-col>
    </v-row>

    <!-- Gráfico Principal -->
    <v-row>
      <v-col
        cols="12"
        md="9"
      >
        <v-card height="100%" outlined>
          <v-card-title class="d-flex align-center justify-space-between">
            <div>
              <span class="text-lg font-weight-semibold">Relatório Estatístico</span>
              <p class="text-xs text--secondary mb-0">
                Distribuição por status de entrega do dia
              </p>
            </div>
            <v-btn
              color="primary"
              outlined
              x-small
              @click="refreshData"
            >
              <v-icon
                left
                size="16"
              >
                {{ icons.mdiRefresh }}
              </v-icon>
              Atualizar
            </v-btn>
          </v-card-title>
          <v-card-text class="chart-container">
            <VueApexCharts
              type="bar"
              height="450"
              :options="chartOptions"
              :series="chartSeries"
            />
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Legenda Lateral -->
      <v-col
        cols="12"
        md="3"
      >
        <v-card height="100%" outlined>
          <v-card-title class="pb-2">
            <span class="text-md font-weight-semibold">Status</span>
          </v-card-title>
          <v-card-text class="pa-3">
            <div
              v-for="(item, index) in statusLegend"
              :key="index"
              class="legend-item d-flex align-center mb-3"
            >
              <div
                class="legend-color"
                :style="{ backgroundColor: item.color }"
              />
              <div class="legend-text">
                <p class="text-sm font-weight-medium mb-0">
                  {{ item.name }}
                </p>
                <p class="text-xs text--secondary mb-0">
                  {{ item.quantity.toLocaleString('pt-BR') }}
                </p>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import {
  mdiCheckCircle,
  mdiCloseCircle,
  mdiClockOutline,
  mdiEmailSend,
  mdiTrendingUp,
  mdiTrendingDown,
  mdiRefresh,
} from '@mdi/js'
import { mapActions } from 'vuex'
import VueApexCharts from 'vue-apexcharts'

export default {
  name: 'ReportsGraphics',
  components: {
    VueApexCharts,
  },
  data() {
    return {
      icons: {
        mdiCheckCircle,
        mdiCloseCircle,
        mdiClockOutline,
        mdiEmailSend,
        mdiTrendingUp,
        mdiTrendingDown,
        mdiRefresh,
      },
      
      // Legenda de Status
      statusLegend: [
        { name: 'Enviado', color: '#2196F3', quantity: 0 },
        { name: 'Entregue', color: '#4CAF50', quantity: 0 },
        { name: 'Falhando', color: '#F44336', quantity: 0 },
        { name: 'Rejeitado', color: '#9C27B0', quantity: 0 },
        { name: 'Blacklist', color: '#424242', quantity: 0 },
        { name: 'Não Entregável', color: '#FF9800', quantity: 0 },
        { name: 'Resposta', color: '#00BCD4', quantity: 0 },
        { name: 'Sem Saldo', color: '#FF5722', quantity: 0 },
      ],

      // Dados do Gráfico Principal
      chartSeries: [{
        name: 'Quantidade',
        data: [0, 0, 0, 0, 0, 0, 0, 0],
      }],
      
      chartOptions: {
        chart: {
          type: 'bar',
          toolbar: {
            show: false,
          },
          offsetX: 0,
          offsetY: 0,
        },
        plotOptions: {
          bar: {
            borderRadius: 0,
            distributed: true,
            horizontal: false,
            columnWidth: '50%',
          },
        },
        colors: ['#2196F3', '#4CAF50', '#F44336', '#9C27B0', '#424242', '#FF9800', '#00BCD4', '#FF5722'],
        dataLabels: {
          enabled: true,
          formatter: val => val.toLocaleString('pt-BR'),
          style: {
            fontSize: '11px',
            fontWeight: 'bold',
          },
        },
        legend: {
          show: false,
        },
        xaxis: {
          categories: [
            'Enviado',
            'Entregue',
            'Falhando',
            'Rejeitado',
            'Blacklist',
            'Não Entregável',
            'Resposta',
            'Sem Saldo',
          ],
          labels: {
            style: {
              fontSize: '11px',
              fontWeight: 500,
            },
            rotate: -45,
            rotateAlways: true,
            hideOverlappingLabels: false,
            trim: false,
          },
          axisBorder: {
            show: true,
          },
          axisTicks: {
            show: true,
          },
        },
        yaxis: {
          title: {
            text: 'Quantidade de SMS',
            style: {
              fontSize: '12px',
              fontWeight: 600,
            },
          },
          labels: {
            formatter: val => val.toLocaleString('pt-BR'),
          },
        },
        tooltip: {
          y: {
            formatter: val => `${val.toLocaleString('pt-BR')} SMS`,
          },
        },
        grid: {
          padding: {
            left: 10,
            right: 10,
            bottom: 10,
          },
        },
      },
    }
  },
  
  async mounted() {
    await this.loadData()
  },
  
  methods: {
    ...mapActions('reportGraphics', ['getStatusDataToday']),
    
    async loadData() {
      try {
        const data = await this.getStatusDataToday()
        
        if (data && data.length > 0) {
          this.updateChartData(data)
        }
      } catch (error) {
        console.error('Erro ao carregar dados:', error)
        this.$store.commit('app/SHOW_SNACKBAR', {
          text: 'Erro ao carregar dados do gráfico',
          color: 'error',
        })
      }
    },
    
    async refreshData() {
      await this.loadData()
      this.$store.commit('app/SHOW_SNACKBAR', {
        text: 'Dados atualizados com sucesso!',
        color: 'success',
      })
    },
    
    updateChartData(data) {
      // Mapeia os dados recebidos do backend
      const statusMap = {
        'Enviado': data.find(d => d.status === 'Enviado')?.quantity || 0,
        'Entregue': data.find(d => d.status === 'Entregue')?.quantity || 0,
        'Falhando': data.find(d => d.status === 'Falhando')?.quantity || 0,
        'Rejeitado': data.find(d => d.status === 'Rejeitado')?.quantity || 0,
        'Blacklist': data.find(d => d.status === 'Blacklist')?.quantity || 0,
        'Não Entregável': data.find(d => d.status === 'Não Entregável')?.quantity || 0,
        'Resposta': data.find(d => d.status === 'Resposta')?.quantity || 0,
        'Sem Saldo': data.find(d => d.status === 'Sem Saldo')?.quantity || 0,
      }
      
      // Atualiza a legenda
      this.statusLegend = this.statusLegend.map(item => ({
        ...item,
        quantity: statusMap[item.name] || 0,
      }))
      
      // Atualiza os dados do gráfico
      this.chartSeries = [{
        name: 'Quantidade',
        data: [
          statusMap['Enviado'],
          statusMap['Entregue'],
          statusMap['Falhando'],
          statusMap['Rejeitado'],
          statusMap['Blacklist'],
          statusMap['Não Entregável'],
          statusMap['Resposta'],
          statusMap['Sem Saldo'],
        ],
      }]
    },
  },
}
</script>

<style scoped>
.card-hover {
  transition: all 0.3s ease;
}

.card-hover:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.chart-container {
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 24px !important;
}

.chart-container > .apexcharts-canvas {
  min-width: 800px;
  margin: 0 auto;
}

.chart-container > .apexcharts-xaxis-label {
  max-width: 100px;
  overflow: visible !important;
}

/* Customiza a scrollbar */
.chart-container::-webkit-scrollbar {
  height: 8px;
}

.chart-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.chart-container::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.chart-container::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* Estilos da Legenda */
.legend-item {
  transition: all 0.2s ease;
  padding: 4px;
  border-radius: 4px;
}

.legend-item:hover {
  background-color: rgba(0, 0, 0, 0.03);
}

.legend-color {
  width: 16px;
  height: 16px;
  border-radius: 3px;
  margin-right: 12px;
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.legend-text {
  flex: 1;
}
</style>
